import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

// @mui
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import Chip from '@mui/material/Chip'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import VisibilityIcon from '@mui/icons-material/Visibility'
import EmailIcon from '@mui/icons-material/Email'
import BarChartIcon from '@mui/icons-material/BarChart'

// MD
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'

// Layout
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Loader from 'examples/Loader'

// Shared table component (same one Users uses)
import CustomDataTable from '../Users/CustomDataTable'

// Services
import { useAppServices } from 'hook/services'

// Build the [last 12 months] dropdown labels — newest first.
const monthOptions = (() => {
  const now = new Date()
  const out = []
  for (let i = 0; i < 12; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1))
    const value = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
    const label = d.toLocaleString('default', { month: 'long', year: 'numeric' })
    out.push({ value, label })
  }
  return out
})()

function CreativeDirectoryAnalytics() {
  const AppService = useAppServices()
  const navigate = useNavigate()

  const [list, setList] = useState([])
  const [loader, setLoader] = useState(true)
  const [search, setSearch] = useState('')
  const [month, setMonth] = useState(monthOptions[0].value)
  const typingTimeoutRef = useRef(null)

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 0,
    totalUsers: 0,
  })

  const fetchAnalytics = async (
    page = pagination.page,
    limit = pagination.limit,
    q = search,
    m = month
  ) => {
    setLoader(true)
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        month: m,
      })
      if (q?.trim()) params.set('search', q.trim())
      const { response } = await AppService.creativeDirectory.listAnalyticsAdmin({
        query: params.toString(),
      })

      if (response?.success) {
        setList(Array.isArray(response.data) ? response.data : [])
        const meta = response.meta || {}
        setPagination({
          page: meta.page || page,
          limit: meta.limit || limit,
          totalPages: meta.pages || 0,
          totalUsers: meta.total || 0,
        })
      } else {
        setList([])
      }
    } catch (err) {
      console.error('❌ Error fetching analytics:', err)
      setList([])
    } finally {
      setLoader(false)
    }
  }

  // Refetch when month changes (debounced search reuses its own effect).
  useEffect(() => {
    fetchAnalytics(1, pagination.limit, search, month)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month])

  useEffect(() => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    typingTimeoutRef.current = setTimeout(() => {
      fetchAnalytics(1, pagination.limit, search, month)
    }, 500)
    return () => clearTimeout(typingTimeoutRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const handlePageChange = (newPage) => {
    fetchAnalytics(newPage, pagination.limit, search, month)
  }
  const handleLimitChange = (newLimit) => {
    fetchAnalytics(1, newLimit, search, month)
  }

  // Aggregate totals across the visible page so the admin sees the at-a-glance
  // numbers without doing math in their head.
  const pageTotals = list.reduce(
    (acc, l) => {
      acc.view += l.analytics?.view || 0
      acc.email += l.analytics?.email_click || 0
      return acc
    },
    { view: 0, email: 0 }
  )

  const dataTableData = {
    columns: [
      { Header: 'Creative', accessor: 'name' },
      { Header: 'Profession', accessor: 'profession' },
      { Header: 'City', accessor: 'city' },
      { Header: 'Tier', accessor: 'tier' },
      { Header: 'Views', accessor: 'views' },
      { Header: 'Email Clicks', accessor: 'emails' },
      { Header: 'Action', accessor: 'action' },
    ],
    rows: list.map((l) => ({
      name: (
        <MDBox display="flex" flexDirection="column">
          <MDTypography variant="button" fontWeight="medium">
            {l.firstName} {l.lastName}
          </MDTypography>
          {l.user?.email ? (
            <MDTypography variant="caption" color="text">
              {l.user.email}
            </MDTypography>
          ) : null}
        </MDBox>
      ),
      profession: l.profession || '—',
      city: l.city || '—',
      tier: (
        <Chip
          label={l.tier}
          size="small"
          color={l.tier === 'featured' ? 'warning' : 'info'}
          sx={{ textTransform: 'uppercase', fontSize: 10 }}
        />
      ),
      views: (
        <MDBox display="flex" alignItems="center" gap={0.5}>
          <VisibilityIcon fontSize="small" sx={{ color: '#65558F' }} />
          <MDTypography variant="button" fontWeight="medium">
            {l.analytics?.view || 0}
          </MDTypography>
        </MDBox>
      ),
      emails: (
        <MDBox display="flex" alignItems="center" gap={0.5}>
          <EmailIcon fontSize="small" sx={{ color: '#E8A04B' }} />
          <MDTypography variant="button" fontWeight="medium">
            {l.analytics?.email_click || 0}
          </MDTypography>
        </MDBox>
      ),
      action: (
        <Tooltip title="View detailed analytics">
          <IconButton
            size="small"
            onClick={() => navigate(`/agency/creative-directory/${l._id}/analytics`)}
            sx={{ color: '#2563EB' }}
          >
            <BarChartIcon />
          </IconButton>
        </Tooltip>
      ),
    })),
  }

  return (
    <DashboardLayout>
      {loader && list.length === 0 ? (
        <Loader />
      ) : (
        <>
          <DashboardNavbar />
          <MDBox pt={6} pb={3}>
            <Grid container spacing={6}>
              <Grid item xs={12}>
                <Card>
                  <MDBox
                    mx={2}
                    py={3}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <MDTypography variant="h6" color="white">
                      Creative Directory — Analytics
                    </MDTypography>
                    <MDBox display="flex" gap={2}>
                      <Chip
                        icon={<VisibilityIcon style={{ color: 'white' }} />}
                        label={`Views (page): ${pageTotals.view}`}
                        sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                      />
                      <Chip
                        icon={<EmailIcon style={{ color: 'white' }} />}
                        label={`Email clicks (page): ${pageTotals.email}`}
                        sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                      />
                    </MDBox>
                  </MDBox>

                  <MDBox px={3} pt={3}>
                    <Grid container spacing={1.5} alignItems="center">
                      <Grid item xs={12} md="auto">
                        <TextField
                          select
                          size="small"
                          label="Month"
                          value={month}
                          onChange={(e) => setMonth(e.target.value)}
                          sx={{ minWidth: 200, backgroundColor: 'white', borderRadius: '8px' }}
                        >
                          {monthOptions.map((m) => (
                            <MenuItem key={m.value} value={m.value}>
                              {m.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md>
                        <TextField
                          variant="outlined"
                          size="small"
                          placeholder="Search by name, profession, city..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          fullWidth
                          sx={{ backgroundColor: 'white', borderRadius: '8px' }}
                        />
                      </Grid>
                    </Grid>
                  </MDBox>

                  <MDBox pt={2} px={3}>
                    {list.length === 0 ? (
                      <MDBox py={6} textAlign="center">
                        <MDTypography variant="button" color="text">
                          No approved listings found for this month.
                        </MDTypography>
                      </MDBox>
                    ) : (
                      <CustomDataTable
                        table={dataTableData}
                        canSearch={false}
                        entriesPerPage={{
                          defaultValue: pagination.limit,
                          entries: [5, 10, 15, 20, 25],
                        }}
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onLimitChange={handleLimitChange}
                      />
                    )}
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          </MDBox>
        </>
      )}
    </DashboardLayout>
  )
}

export default CreativeDirectoryAnalytics
